import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/mockData';
import './index.css';

const About = () => {
  const [aboutData, setAboutData] = useState(null);
  const [stats, setStats] = useState({
    yearsRunning: 0,
    postsPublished: 0,
    communityMembers: 0,
    catsHelped: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('story');
  const [teamMembers, setTeamMembers] = useState([]);
  const [catFacts, setCatFacts] = useState([]);
  const [activeFact, setActiveFact] = useState(0);
  const [statsAnimated, setStatsAnimated] = useState(false);

  // 初始化数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.getAboutPage();
        if (response.success) {
          const data = response.data;
          setAboutData(data);
          setTeamMembers(data.team || []);
          setStats(data.stats || {
            yearsRunning: 3,
            postsPublished: 35,
            communityMembers: 1200,
            catsHelped: 50
          });
        }

        // 获取猫咪冷知识
        const facts = [
          "猫咪的鼻子纹路是独一无二的，就像人类的指纹",
          "猫咪可以发出超过100种不同的声音",
          "猫咪用胡须来测量周围空间的宽度",
          "猫咪的睡眠时间占生命的2/3",
          "猫咪的跳跃高度可以达到自己身高的5倍",
          "每只猫咪的鼻纹都是独一无二的",
          "猫咪用尾巴来表达情绪",
          "猫咪的听力比狗和人类都要灵敏"
        ];
        setCatFacts(facts);
      } catch (error) {
        console.error('加载关于页面数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 滚动监听，用于数字动画
  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.querySelector('.stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8 && !statsAnimated) {
          setStatsAnimated(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [statsAnimated]);

  // 猫咪冷知识轮播
  useEffect(() => {
    if (catFacts.length > 0) {
      const interval = setInterval(() => {
        setActiveFact((prev) => (prev + 1) % catFacts.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [catFacts]);

  if (loading) {
    return (
      <div className="about-loading">
        <div className="loading-cat">
          <div className="cat">😺</div>
          <div className="loading-text">猫咪正在准备自我介绍...</div>
          <div className="loading-paws">
            <span className="paw">🐾</span>
            <span className="paw">🐾</span>
            <span className="paw">🐾</span>
          </div>
        </div>
      </div>
    );
  }

  // 渲染HTML内容
  const renderHTML = (html) => {
    return { __html: html };
  };

  return (
    <div className="about-page">
      {/* 英雄区域 */}
      <section className="about-hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="title-cat">🐱</span>
              {aboutData?.title || '关于我们'}
            </h1>
            <p className="hero-subtitle">
              一个由爱猫人士创建的温馨小站，分享与猫咪共度的美好时光
            </p>
            <div className="hero-cats">
              <div className="floating-cat cat-1">😸</div>
              <div className="floating-cat cat-2">😻</div>
              <div className="floating-cat cat-3">🐈</div>
              <div className="floating-cat cat-4">🐈‍⬛</div>
            </div>
          </div>
        </div>
      </section>

      {/* 页面导航标签 */}
      <div className="about-tabs">
        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'story' ? 'active' : ''}`}
            onClick={() => setActiveTab('story')}
          >
            <span className="tab-icon">📖</span>
            我们的故事
          </button>
          <button 
            className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <span className="tab-icon">👥</span>
            我们的团队
          </button>
          <button 
            className={`tab-button ${activeTab === 'mission' ? 'active' : ''}`}
            onClick={() => setActiveTab('mission')}
          >
            <span className="tab-icon">🎯</span>
            我们的使命
          </button>
          <button 
            className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            <span className="tab-icon">📧</span>
            联系我们
          </button>
        </div>
      </div>

      {/* 主要内容 */}
      <main className="about-content">
        {/* 统计区域 */}
        <section className="stats-section">
          <div className="stats-container">
            <div className="stats-header">
              <h2 className="stats-title">
                <span className="stats-icon">📊</span>
                数字背后的故事
              </h2>
              <p className="stats-subtitle">每一份数据都代表着我们对猫咪的热爱</p>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📅</div>
                <div className="stat-info">
                  <div className="stat-number" data-count={stats.yearsRunning}>
                    {statsAnimated ? stats.yearsRunning : 0}
                  </div>
                  <div className="stat-label">年持续记录</div>
                </div>
                <div className="stat-cat">🐱</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📝</div>
                <div className="stat-info">
                  <div className="stat-number" data-count={stats.postsPublished}>
                    {statsAnimated ? stats.postsPublished : 0}+
                  </div>
                  <div className="stat-label">篇原创文章</div>
                </div>
                <div className="stat-cat">📚</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <div className="stat-number" data-count={stats.communityMembers}>
                    {statsAnimated ? stats.communityMembers : 0}+
                  </div>
                  <div className="stat-label">位猫友加入</div>
                </div>
                <div className="stat-cat">😸</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">❤️</div>
                <div className="stat-info">
                  <div className="stat-number" data-count={stats.catsHelped}>
                    {statsAnimated ? stats.catsHelped : 0}+
                  </div>
                  <div className="stat-label">只猫咪受益</div>
                </div>
                <div className="stat-cat">🐾</div>
              </div>
            </div>
          </div>
        </section>

        {/* 我们的故事 */}
        {activeTab === 'story' && (
          <section className="story-section">
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">📖</span>
                  我们的故事
                </h2>
                <p className="section-subtitle">从一个简单的想法，到温暖的爱猫社区</p>
              </div>
              <div className="story-content">
                <div className="story-text">
                  <div 
                    className="html-content"
                    dangerouslySetInnerHTML={renderHTML(aboutData?.content || '')}
                  />
                </div>
                <div className="story-image">
                  <div className="cat-timeline">
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-year">2020年</div>
                        <div className="timeline-text">喵星人日记正式上线</div>
                        <div className="timeline-cat">🐱</div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-year">2021年</div>
                        <div className="timeline-text">社区成员突破1000人</div>
                        <div className="timeline-cat">😸</div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-year">2022年</div>
                        <div className="timeline-text">发起猫咪救助计划</div>
                        <div className="timeline-cat">😻</div>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-year">2023年</div>
                        <div className="timeline-text">文章总数突破30篇</div>
                        <div className="timeline-cat">🐈</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 我们的团队 */}
        {activeTab === 'team' && (
          <section className="team-section">
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">👥</span>
                  我们的团队
                </h2>
                <p className="section-subtitle">一群热爱猫咪的普通人，做着不普通的事</p>
              </div>
              <div className="team-grid">
                {teamMembers.map((member, index) => (
                  <div 
                    key={member.id} 
                    className="team-card"
                    style={{ '--delay': index * 0.1 + 's' }}
                  >
                    <div className="member-avatar">
                      <img 
                        src={member.avatar} 
                        alt={member.name}
                        className="avatar-image"
                      />
                      <div className="avatar-decoration">
                        <div className="cat-ear left"></div>
                        <div className="cat-ear right"></div>
                      </div>
                    </div>
                    <div className="member-info">
                      <h3 className="member-name">{member.name}</h3>
                      <div className="member-role">{member.role}</div>
                      <p className="member-bio">{member.bio}</p>
                    </div>
                    <div className="member-stats">
                      <div className="member-stat">
                        <span className="stat-icon">📝</span>
                        <span className="stat-text">15+ 文章</span>
                      </div>
                      <div className="member-stat">
                        <span className="stat-icon">❤️</span>
                        <span className="stat-text">2 只猫咪</span>
                      </div>
                      <div className="member-stat">
                        <span className="stat-icon">🎓</span>
                        <span className="stat-text">5+ 年经验</span>
                      </div>
                    </div>
                    <div className="member-cat">
                      <div className="cat-emoji">🐾</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 我们的使命 */}
        {activeTab === 'mission' && (
          <section className="mission-section">
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">🎯</span>
                  我们的使命
                </h2>
                <p className="section-subtitle">让每一只猫咪都能得到更好的关爱</p>
              </div>
              <div className="mission-content">
                <div className="mission-cards">
                  <div className="mission-card">
                    <div className="card-icon">📚</div>
                    <h3 className="card-title">知识分享</h3>
                    <p className="card-text">分享科学的养猫知识，帮助猫主人更好地理解猫咪的需求和行为</p>
                  </div>
                  <div className="mission-card">
                    <div className="card-icon">❤️</div>
                    <h3 className="card-title">社区互助</h3>
                    <p className="card-text">建立温暖的社区环境，让猫友们可以互相帮助，分享经验</p>
                  </div>
                  <div className="mission-card">
                    <div className="card-icon">🐾</div>
                    <h3 className="card-title">动物保护</h3>
                    <p className="card-text">倡导科学的动物保护理念，反对虐待，促进人猫和谐共处</p>
                  </div>
                  <div className="mission-card">
                    <div className="card-icon">🌟</div>
                    <h3 className="card-title">持续学习</h3>
                    <p className="card-text">不断学习和探索猫咪的世界，分享最新的研究成果和发现</p>
                  </div>
                </div>
                <div className="mission-vision">
                  <h3 className="vision-title">我们的愿景</h3>
                  <div className="vision-content">
                    <div className="vision-text">
                      我们希望建立一个世界，在那里每只猫咪都能得到理解、尊重和爱护，
                      每个人都能在猫咪的陪伴下感受到温暖和快乐。
                    </div>
                    <div className="vision-cat">😸</div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 联系我们 */}
        {activeTab === 'contact' && (
          <section className="contact-section">
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">📧</span>
                  联系我们
                </h2>
                <p className="section-subtitle">我们很乐意听到你的声音！</p>
              </div>
              <div className="contact-content">
                <div className="contact-info">
                  <div className="info-card">
                    <div className="info-icon">📧</div>
                    <h3 className="info-title">电子邮件</h3>
                    <p className="info-text">hello@meowdiary.com</p>
                    <div className="info-cat">🐱</div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon">💬</div>
                    <h3 className="info-title">社交媒体</h3>
                    <div className="social-links">
                      <a href="#" className="social-link twitter">🐦 Twitter</a>
                      <a href="https://weibo.com/catdiary" className="social-link weibo">🐱 微博</a>
                      <a href="#" className="social-link wechat">💬 微信</a>
                    </div>
                  </div>
                  <div className="info-card">
                    <div className="info-icon">📝</div>
                    <h3 className="info-title">投稿合作</h3>
                    <p className="info-text">submit@meowdiary.com</p>
                    <div className="info-cat">✏️</div>
                  </div>
                </div>
                <div className="contact-form">
                  <h3 className="form-title">发送消息给我们</h3>
                  <form className="message-form">
                    <div className="form-group">
                      <input type="text" placeholder="你的名字" className="form-input" />
                    </div>
                    <div className="form-group">
                      <input type="email" placeholder="你的邮箱" className="form-input" />
                    </div>
                    <div className="form-group">
                      <textarea placeholder="你想说什么呢？猫咪很想知道！" className="form-textarea" rows="4"></textarea>
                    </div>
                    <button type="submit" className="submit-button">
                      <span className="submit-icon">📨</span>
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 猫咪冷知识 */}
        <section className="cat-facts-section">
          <div className="facts-container">
            <div className="facts-header">
              <h2 className="facts-title">
                <span className="facts-icon">🔍</span>
                猫咪冷知识
              </h2>
              <p className="facts-subtitle">你可能不知道的猫咪小秘密</p>
            </div>
            <div className="facts-carousel">
              <div className="fact-item active">
                <div className="fact-cat">😺</div>
                <p className="fact-text">{catFacts[activeFact] || "猫咪的鼻子纹路是独一无二的"}</p>
              </div>
            </div>
            <div className="facts-controls">
              {catFacts.map((_, index) => (
                <button
                  key={index}
                  className={`fact-dot ${index === activeFact ? 'active' : ''}`}
                  onClick={() => setActiveFact(index)}
                >
                  <span className="dot-cat">🐾</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 加入我们 */}
        <section className="join-section">
          <div className="join-container">
            <div className="join-content">
              <h2 className="join-title">
                <span className="join-icon">🐱</span>
                加入喵星人日记
              </h2>
              <p className="join-subtitle">
                无论你是资深猫奴、新手铲屎官，还是单纯喜欢猫咪的朋友，
                这里都欢迎你的加入！
              </p>
              <div className="join-buttons">
                <Link to="/write" className="join-button primary">
                  <span className="button-icon">✏️</span>
                  开始写作
                </Link>
                <Link to="/" className="join-button secondary">
                  <span className="button-icon">📚</span>
                  浏览文章
                </Link>
                <button className="join-button tertiary">
                  <span className="button-icon">❤️</span>
                  支持我们
                </button>
              </div>
            </div>
            <div className="join-cats">
              <div className="join-cat cat-1">😸</div>
              <div className="join-cat cat-2">😻</div>
              <div className="join-cat cat-3">🐈</div>
            </div>
          </div>
        </section>
      </main>

      {/* 底部猫咪足迹 */}
      <div className="about-footer">
        <div className="footer-content">
          <div className="footer-cats">
            <div className="footer-cat">🐱</div>
            <div className="footer-cat">😸</div>
            <div className="footer-cat">😻</div>
          </div>
          <p className="footer-text">
            感谢每一份对猫咪的爱，让我们一起创造更多美好回忆
          </p>
          <div className="footer-links">
            <Link to="/" className="footer-link">🏠 首页</Link>
            <Link to="/archive" className="footer-link">📚 归档</Link>
            <Link to="/write" className="footer-link">✏️ 写作</Link>
            <a href="#top" className="footer-link">🐱 回到顶部</a>
          </div>
        </div>
      </div>

      {/* 浮动装饰猫咪 */}
      <div className="floating-decoration">
        <div className="decoration-cat cat-1">😺</div>
        <div className="decoration-cat cat-2">🐾</div>
        <div className="decoration-cat cat-3">😸</div>
      </div>
    </div>
  );
};

export default About;